import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { formatEmployeesNumber } from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';

import { AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment } from '../../graphql/fragment/about-us-subpage-facts-fragment.gql-types';

import employeesIllustration from './assets/people.svg';
import type { CommonFactsItem } from './create-facts-item';
import {
  createFactsItemFoundingYear,
  createFactsItemImprint,
  createFactsItemWebsiteUrl,
} from './create-facts-item';
import { Facts } from './facts';

type FactsCompanyProps = {
  facts: AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment;
};

export const FactsCompany: FC<FactsCompanyProps> = ({ facts }) => {
  const companySizeRange = facts?.companyData?.companySizeRange;
  const { isEditing } = useEditContext();
  const intl = useIntl();
  const items: CommonFactsItem[] = [];

  if (facts.foundingYear || isEditing) {
    items.push(createFactsItemFoundingYear(facts.foundingYear));
  }

  if (companySizeRange && companySizeRange.min !== companySizeRange?.max) {
    items.push({
      type: 'employeesNumber',
      values: {
        employeesNumber: formatEmployeesNumber(companySizeRange, intl),
      },
      link: { href: 'employees' },
      illustration: employeesIllustration,
      translationKey: 'EP_EMPLOYEES_COUNT',
    });
  } else if (isEditing) {
    items.push({
      type: 'employeesNumber',
      illustration: employeesIllustration,
      translationKey: 'EP_EMPLOYEES_NUMBER_EMPTY_STATE',
      showEmptyState: true,
    });
  }

  if (facts.websiteUrl || isEditing) {
    items.push(createFactsItemWebsiteUrl(facts.websiteUrl));
  }

  if (facts.imprint || isEditing) {
    items.push(createFactsItemImprint(facts.imprint));
  }

  const localizationKey = facts.companyData?.industry?.localizationKey;
  const categories: string[] = localizationKey
    ? [
        intl.formatMessage({
          id: localizationKey,
          defaultMessage: localizationKey,
        }),
      ]
    : [];

  return (
    <Facts
      headlineKey="EP_COMPANY_SUMMARY"
      items={items}
      categoriesHeadlineKey="EP_COMPANY_INDUSTRY"
      categories={categories}
      facts={facts}
    />
  );
};
